import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Heart, Share2, TrendingUp, MapPin, ArrowLeft, FileText, Image as ImageIcon,
  DollarSign, AlertTriangle, UserCheck, Calendar, Target, MessageCircle, ShieldAlert, Flag
} from 'lucide-react'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Skeleton } from '../components/ui/Skeleton'
import TabGroup from '../components/ui/TabGroup'
import DonationModal from '../components/donations/DonationModal'
import DonationsList from '../components/donations/DonationsList'
import QASection from '../components/qa/QASection'
import WithdrawalSection from '../components/withdrawals/WithdrawalSection'
import ShareModal from '../components/ui/ShareModal'
import SignInModal from '../components/auth/SignInModal'
import MultiCurrencyFunding from '../components/projects/MultiCurrencyFunding'
import { useAuth } from '../hooks/useAuth'
import projectService from '../services/projectService'
import toast from 'react-hot-toast'
import { getCurrencySymbol } from '../constants/index.js'
import ReportProjectModal from '../components/projects/ReportProjectModal'

const ProjectView = () => {
  const { slug } = useParams()
  const navigate = useNavigate()
  const { user, isAuthenticated } = useAuth()
  const [isFavorited, setIsFavorited] = useState(false)
  const [isDonationModalOpen, setIsDonationModalOpen] = useState(false)
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)
  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false)
  const [isReportModalOpen, setIsReportModalOpen] = useState(false)

  const { data: projectData, isLoading, error, refetch } = useQuery({
    queryKey: ['project', slug],
    queryFn: () => projectService.getProjectBySlug(slug),
    enabled: !!slug,
  })

  const project = projectData?.data

  // Memoized calculations
  const fundingStats = useMemo(() => {
    if (!project) return null

    const current = parseFloat(project.current_funding || 0);
    const goal = parseFloat(project.funding_goal || 0);
    const progress = project.funding_progress || 0;
    const donors = project.donor_count || 0;


    return {
      current,
      goal,
      progress,
      donors,
      daysLeft: project.days_remaining || 0
    }
  }, [project])

  const handleShare = () => {
    setIsShareModalOpen(true)
    // Track share intent
    try {
      projectService.trackShare(project.id)
    } catch (error) {
      console.log('Failed to track share:', error)
    }
  }

  const handleFavorite = () => {
    if (!isAuthenticated) {
      setIsSignInModalOpen(true);
      return;
    }
    setIsFavorited(!isFavorited)
    // TODO: Implement favorite functionality
  }

  // Handle donation modal
  const handleDonateClick = () => {
    if (!isAuthenticated) {
      setIsSignInModalOpen(true);
      return;
    }
    if (project.status === 'completed') {
      toast.error('This campaign has ended and is no longer accepting donations');
      return;
    }
    setIsDonationModalOpen(true);
  }

  // Handle report click
  const handleReportClick = () => {
    if (!isAuthenticated) {
      setIsSignInModalOpen(true);
      return;
    }
    setIsReportModalOpen(true);
  }

  // Handle successful donation
  const handleDonationSuccess = (result) => {
    // Refetch project data to update funding stats
    refetch();

    // Show success message
    if (result?.data?.badgeUpgraded) {
      toast.success(
        `Thank you for your donation! 🎉 You've earned the ${result.data.newBadge} badge!`,
        { duration: 6000 }
      );
    } else {
      toast.success('Thank you for your donation! Your contribution makes a difference.');
    }
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FileText },
    { id: 'gallery', label: 'Gallery', icon: ImageIcon },
    { id: 'funding', label: 'Funding', icon: DollarSign },
    { id: 'team', label: 'Team', icon: UserCheck },
    { id: 'risks', label: 'Risks', icon: AlertTriangle },
    { id: 'qa', label: 'Q&A', icon: MessageCircle }
  ]

  // Enhanced tab content renderer
  const renderTabContent = (tabId) => {
    const tabContentVariants = {
      hidden: { opacity: 0, y: 20 },
      visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
    }

    const content = {
      overview: (
        <div className="space-y-6">
          {project.video_url && (
            <Card className="border-0 bg-black overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300 rounded-2xl">
              <video
                src={project.video_url}
                poster={project.cover_image}
                controls
                className="w-full aspect-video object-contain"
              />
            </Card>
          )}

          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow duration-300">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2">
                <div className="w-8 h-8 bg-primary-teal/10 rounded-lg flex items-center justify-center">
                  <FileText className="w-4 h-4 text-primary-teal" />
                </div>
                About This Project
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-neutral max-w-none">
                <p className="text-neutral-700 leading-relaxed whitespace-pre-wrap">
                  {project.story}
                </p>
              </div>
            </CardContent>
          </Card>

          {project.is_app_listing && project.app_url && (
            <Card className="border-0 shadow-sm hover:shadow-md transition-shadow duration-300 bg-gradient-to-r from-primary-teal/5 to-transparent">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-primary-teal/10 rounded-lg flex items-center justify-center">
                    <Target className="w-4 h-4 text-primary-teal" />
                  </div>
                  App Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-white rounded-lg border border-primary-teal/10">
                  <h4 className="font-semibold text-neutral-900 mb-2">App URL</h4>
                  <a
                    href={project.app_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-teal hover:underline break-all font-medium"
                  >
                    {project.app_url}
                  </a>
                </div>
                <div className="p-4 bg-white rounded-lg border border-primary-teal/10">
                  <h4 className="font-semibold text-neutral-900 mb-2">Funding Purpose</h4>
                  <p className="text-neutral-700">{project.funding_for_display}</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      ),

      gallery: (
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary-teal/10 rounded-lg flex items-center justify-center">
                <ImageIcon className="w-4 h-4 text-primary-teal" />
              </div>
              Project Gallery
            </CardTitle>
          </CardHeader>
          <CardContent>
            {project.gallery_images?.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {project.gallery_images.map((image, index) => (
                  <motion.div
                    key={index}
                    className="aspect-video rounded-xl overflow-hidden bg-neutral-100"
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.2 }}
                  >
                    <img
                      src={image}
                      alt={`Gallery ${index + 1}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-neutral-500">
                <ImageIcon className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg">No gallery images available</p>
              </div>
            )}
          </CardContent>
        </Card>
      ),

      funding: (
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary-teal/10 rounded-lg flex items-center justify-center">
                <DollarSign className="w-4 h-4 text-primary-teal" />
              </div>
              How Funds Will Be Used
            </CardTitle>
          </CardHeader>
          <CardContent>
            {project.funding_use?.length > 0 ? (
              <div className="space-y-3">
                {project.funding_use.map((item, index) => (
                  <motion.div
                    key={index}
                    className="flex items-center justify-between p-4 bg-gradient-to-r from-neutral-50 to-neutral-100/50 rounded-xl hover:shadow-sm transition-shadow"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="flex-1">
                      <h4 className="font-semibold text-neutral-900">{item.category}</h4>
                      <p className="text-sm text-neutral-600">{item.description}</p>
                    </div>
                    <div className="text-right ml-4">
                      <div className="text-xl font-bold text-neutral-900">
                        {getCurrencySymbol(project.currency)}{new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(item.amount || 0)}
                      </div>
                      <div className="text-sm text-primary-teal font-medium">
                        {item.percentage}%
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-neutral-500">
                <DollarSign className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg">No funding breakdown available</p>
              </div>
            )}
          </CardContent>
        </Card>
      ),

      team: (
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary-teal/10 rounded-lg flex items-center justify-center">
                <UserCheck className="w-4 h-4 text-primary-teal" />
              </div>
              Meet the Team
            </CardTitle>
          </CardHeader>
          <CardContent>
            {project.team_members?.length > 0 ? (
              <div className="space-y-4">
                {project.team_members.map((member, index) => (
                  <motion.div
                    key={index}
                    className="flex items-center space-x-4 p-4 bg-gradient-to-r from-neutral-50 to-neutral-100/50 rounded-xl hover:shadow-sm transition-shadow"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="w-12 h-12 bg-gradient-to-br from-primary-teal to-primary-teal/80 rounded-full flex items-center justify-center text-white font-semibold shadow-lg">
                      {member.name.charAt(0)}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-neutral-900">{member.name}</h4>
                      <p className="text-sm text-neutral-600">{member.email}</p>
                      {member.social_link && (
                        <a
                          href={member.social_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-teal hover:underline text-sm font-medium"
                        >
                          Social Profile
                        </a>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-neutral-500">
                <UserCheck className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg">No team information available</p>
              </div>
            )}
          </CardContent>
        </Card>
      ),

      risks: (
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <div className="w-8 h-8 bg-secondary-red/10 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-4 h-4 text-secondary-red" />
              </div>
              Risks & Challenges
            </CardTitle>
          </CardHeader>
          <CardContent>
            {project.risks_challenges?.trim() ? (
              <div className="p-4 bg-gradient-to-r from-secondary-red/5 to-transparent rounded-xl border-l-4 border-secondary-red/30">
                <p className="text-neutral-700 leading-relaxed whitespace-pre-wrap">
                  {project.risks_challenges}
                </p>
              </div>
            ) : (
              <div className="text-center py-12 text-neutral-500">
                <AlertTriangle className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg">No risks and challenges information available</p>
              </div>
            )}
          </CardContent>
        </Card>
      ),

      qa: (
        <QASection
          project={project}
          onAuthRequired={() => setIsSignInModalOpen(true)}
        />
      )
    }

    return (
      <motion.div variants={tabContentVariants} initial="hidden" animate="visible">
        {content[tabId]}
      </motion.div>
    )
  }

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-64 w-full rounded-xl" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-40 w-full rounded-xl" />
          </div>
          <Skeleton className="h-96 w-full rounded-xl" />
        </div>
      </div>
    )
  }

  if (error || !project) {
    return (
      <motion.div
        className="text-center py-24"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="max-w-md mx-auto">
          <div className="w-20 h-20 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="w-10 h-10 text-neutral-400" />
          </div>
          <h2 className="text-2xl font-bold text-neutral-900 mb-4">Project Not Found</h2>
          <p className="text-neutral-600 mb-8">
            The project you're looking for doesn't exist or has been removed.
          </p>
          <Button onClick={() => navigate('/projects')} size="lg">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Projects
          </Button>
        </div>
      </motion.div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="space-y-6 pb-8"
    >
      {/* Enhanced Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate('/projects')}
        className="mb-4 hover:bg-neutral-100 transition-colors"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Projects
      </Button>

      {/* Premium Hero Section */}
      <motion.div
        className="relative h-64 sm:h-72 lg:h-80 rounded-2xl overflow-hidden bg-gradient-to-br from-neutral-100 to-neutral-200 shadow-lg"
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        {project.cover_image ? (
          <img
            src={project.cover_image}
            alt={project.title}
            className="w-full h-full object-cover"
            loading="eager"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-neutral-400">
            <TrendingUp className="w-16 h-16" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

        {/* Enhanced Hero Content */}
        <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <Badge variant="secondary" className="bg-white/90 backdrop-blur-sm text-neutral-700 font-medium">
                  {project.category}
                </Badge>
                {project.is_app_listing && (
                  <Badge variant="default" className="bg-primary-teal/90 backdrop-blur-sm text-white">
                    App
                  </Badge>
                )}
              </div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-2 line-clamp-2">
                {project.title}
              </h1>
              <div className="flex items-center text-white/90 text-sm sm:text-base">
                <MapPin className="w-4 h-4 mr-1 flex-shrink-0" />
                <span className="truncate">{project.country}</span>
              </div>
            </div>

            {/* Enhanced Action Buttons */}
            <div className="flex items-center gap-2 sm:ml-4">
              <Button
                variant="ghost"
                size="icon"
                className="h-10 w-10 bg-white/20 backdrop-blur-md hover:bg-white/30 text-white border border-white/20 transition-all duration-200"
                onClick={handleShare}
                title="Share Project"
              >
                <Share2 className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className={`h-10 w-10 bg-white/20 backdrop-blur-md hover:bg-white/30 border border-white/20 transition-all duration-200 ${isFavorited ? 'text-secondary-red' : 'text-white'
                  }`}
                onClick={handleFavorite}
                title={isFavorited ? "Remove from Favorites" : "Add to Favorites"}
              >
                <Heart className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`} />
              </Button>
              {user?.id !== project.owner_id && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-10 w-10 bg-white/20 backdrop-blur-md hover:bg-secondary-red text-secondary-red border border-white/20 transition-all duration-200"
                  onClick={handleReportClick}
                  title="Report Project"
                >
                  <Flag className="w-4 h-4 fill-current" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Enhanced Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
        {/* Left Column - Enhanced Project Details */}
        <div className="lg:col-span-2">
          <TabGroup tabs={tabs} defaultTab="overview">
            {renderTabContent}
          </TabGroup>
        </div>

        {/* Right Column - Premium Sticky Funding Card */}
        <div className="space-y-4 lg:sticky lg:top-24 lg:z-10 self-start">
          <motion.div
            className="transition-all duration-300"
            initial={{ x: 20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            {/* Enhanced Funding Progress Card */}
            <Card className="border-2 border-primary-teal/20 bg-white shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardContent className="p-6">
                {/* Multi-Currency Funding Display */}
                <MultiCurrencyFunding
                  project={project}
                  currencyBalances={project.currency_balances || []}
                />

                {/* Enhanced Stats Grid */}
                <div className="grid grid-cols-2 gap-4 mt-6 mb-6">
                  <div className="text-center p-3 bg-gradient-to-br from-neutral-50 to-neutral-100 rounded-xl">
                    <div className="text-2xl font-bold text-neutral-900">
                      {fundingStats?.donors}
                    </div>
                    <div className="text-xs text-neutral-600 uppercase tracking-wide">donors</div>
                  </div>
                  <div className="text-center p-3 bg-gradient-to-br from-neutral-50 to-neutral-100 rounded-xl">
                    <div className="text-2xl font-bold text-neutral-900 flex items-center justify-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {fundingStats?.daysLeft}
                    </div>
                    <div className="text-xs text-neutral-600 uppercase tracking-wide">days left</div>
                  </div>
                </div>

                {/* Enhanced Action Buttons */}
                <div className="space-y-3">
                  <Button
                    onClick={handleDonateClick}
                    disabled={project.status === 'completed'}
                    className={`w-full h-12 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${project.status === 'completed'
                      ? 'opacity-60 cursor-not-allowed bg-neutral-400 hover:bg-neutral-400'
                      : ''
                      }`}
                    size="lg"
                  >
                    <TrendingUp className="w-5 h-5 mr-2" />
                    {project.status === 'completed' ? 'Campaign Ended' : 'Donate Now'}
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full h-10 hover:bg-neutral-50 transition-colors"
                    onClick={handleShare}
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Share Project
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Enhanced Creator Info */}
            <Card className="shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Project Creator</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex items-center space-x-3">
                  {project.owner?.profile_image ? (
                    <img
                      src={project.owner.profile_image}
                      alt={project.owner.full_name || project.owner.pi_username}
                      className="w-12 h-12 rounded-full object-cover shadow-md"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-gradient-to-br from-primary-teal to-primary-teal/80 rounded-full flex items-center justify-center text-white font-bold shadow-md">
                      {project.owner?.full_name?.charAt(0) || project.owner?.pi_username?.charAt(0) || 'U'}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-neutral-900 truncate">
                      {project.owner?.full_name || project.owner?.pi_username}
                    </h4>
                    <p className="text-sm text-neutral-600">
                      {project.owner?.badge_level ? `${project.owner.badge_level} Supporter` : 'Project Creator'}
                    </p>
                    {project.owner?.total_donated && parseFloat(project.owner.total_donated) > 0 && (
                      <p className="text-sm text-primary-teal font-medium">
                        Total donated: π{new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(project.owner.total_donated)}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <DonationsList projectId={project.id} limit={5} />

            {/* Report Warning for Owner */}
            {user?.id === project.owner_id && project.report_summary && (
              <Card className="mt-4 border-2 border-secondary-red/20 bg-secondary-red/5">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-secondary-red/10 rounded-lg">
                      <Flag className="w-5 h-5 text-secondary-red" />
                    </div>
                    <div>
                      <h4 className="font-bold text-secondary-red text-sm">Project Reported</h4>
                      <p className="text-xs text-neutral-600 mt-1">
                        Your project has {project.report_summary.total_reports} report(s). 
                        Reasons: {project.report_summary.reasons.join(', ')}.
                      </p>
                      {project.status === 'suspended' && (
                        <p className="text-xs font-bold text-secondary-red mt-2">
                          Donations are currently frozen due to multiple reports.
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </motion.div>
        </div>
      </div>

      {/* Withdrawal Section (only visible to project owner) */}
      <WithdrawalSection project={project} />

      {/* Donation Modal */}
      <DonationModal
        project={project}
        isOpen={isDonationModalOpen}
        onClose={() => setIsDonationModalOpen(false)}
        onSuccess={handleDonationSuccess}
      />

      {/* Share Modal */}
      <ShareModal
        project={project}
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
      />

      {/* Sign In Modal */}
      <SignInModal
        isOpen={isSignInModalOpen}
        onClose={() => setIsSignInModalOpen(false)}
      />

      {/* Report Modal */}
      <ReportProjectModal
        project={project}
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
      />
    </motion.div>
  )
}

export default ProjectView
